import { useCoreServices } from '@bangle.io/context';
import { $collabStatus } from '@bangle.io/editor';
import { Button, cn } from '@bangle.io/ui-components';
import { useAtomValue } from 'jotai';
import {
  AlertTriangle,
  Check,
  LoaderCircle,
  Unplug,
  WifiOff,
} from 'lucide-react';
import React from 'react';
import {
  getNoteSaveLocationLabel,
  type NoteSavePillKind,
  resolveNoteSavePill,
} from '../../common/note-save-status-copy';
import { useEditorSaveStatus } from '../../common/use-editor-save-status';

/**
 * Always-visible durable-write / connection indicator for the open note.
 * Cloud notes fold collab transport into the same pill (Connecting,
 * Disconnected, …) so save feedback stays in one place.
 */
export function NoteSaveStatus({ wsPath }: { wsPath: string }) {
  const { editorEngine, workspaceState } = useCoreServices();
  const status = useEditorSaveStatus(editorEngine, wsPath);
  const collabStatus = useAtomValue($collabStatus);
  const workspaces = useAtomValue(workspaceState.$workspaces);
  const currentWsName = useAtomValue(workspaceState.$currentWsName);
  const storageType = workspaces.find((ws) => ws.name === currentWsName)?.type;
  const pill = resolveNoteSavePill({
    editorStatus: status.status,
    collabStatus,
    storageType,
  });

  if (pill.kind === 'failed') {
    return (
      <div
        className="desktop-titlebar-no-drag flex max-w-[min(100%,20rem)] shrink-0 items-center gap-1.5"
        data-testid="note-save-status"
        data-save-status="failed"
        title={pill.title}
      >
        <span
          className={cn(
            'inline-flex min-w-0 items-center gap-1.5 rounded-md border border-destructive/50 bg-destructive px-2 py-0.5 font-medium text-destructive-foreground text-xs',
          )}
          role="status"
          aria-live="assertive"
        >
          <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{pill.label}</span>
        </span>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          className="h-7 shrink-0 px-2 text-xs"
          data-testid="note-save-status-retry"
          onClick={() => {
            editorEngine.retryFailedSave(wsPath);
          }}
        >
          {t.app.editor.saveStatus.retry}
        </Button>
      </div>
    );
  }

  if (isBusyKind(pill.kind)) {
    return (
      <div
        className="desktop-titlebar-no-drag flex shrink-0 items-center"
        data-testid="note-save-status"
        data-save-status={pill.kind}
        title={pill.title}
      >
        <span
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 font-medium text-amber-800 text-xs dark:text-amber-200"
          role="status"
          aria-live="polite"
        >
          <LoaderCircle
            className="size-3.5 shrink-0 animate-spin"
            aria-hidden
          />
          <span>{pill.label}</span>
        </span>
      </div>
    );
  }

  if (isWarnKind(pill.kind)) {
    const Icon = pill.kind === 'offline' ? WifiOff : Unplug;
    return (
      <div
        className="desktop-titlebar-no-drag flex shrink-0 items-center"
        data-testid="note-save-status"
        data-save-status={pill.kind}
        title={pill.title}
      >
        <span
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-medium text-amber-900 text-xs dark:text-amber-200"
          role="status"
          aria-live="polite"
        >
          <Icon className="size-3.5 shrink-0" aria-hidden />
          <span>{pill.label}</span>
        </span>
      </div>
    );
  }

  if (pill.kind === 'error') {
    return (
      <div
        className="desktop-titlebar-no-drag flex shrink-0 items-center"
        data-testid="note-save-status"
        data-save-status="error"
        title={pill.title}
      >
        <span
          className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-0.5 font-medium text-destructive text-xs"
          role="status"
          aria-live="assertive"
        >
          <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
          <span>{pill.label}</span>
        </span>
      </div>
    );
  }

  return (
    <div
      className="desktop-titlebar-no-drag flex min-w-0 shrink-0 items-center"
      data-testid="note-save-status"
      data-save-status="clean"
      title={pill.title}
    >
      <span
        className="inline-flex min-w-0 items-center gap-1.5 rounded-md border border-emerald-600/25 bg-emerald-600/10 px-2 py-0.5 text-emerald-800 text-xs dark:text-emerald-300"
        role="status"
        aria-live="polite"
      >
        <Check className="size-3.5 shrink-0" aria-hidden />
        <span className="truncate">
          {pill.label}
          {pill.showLocation ? (
            <span className="text-emerald-800/70 dark:text-emerald-300/70">
              {' '}
              · {getNoteSaveLocationLabel(storageType)}
            </span>
          ) : null}
        </span>
      </span>
    </div>
  );
}

function isBusyKind(kind: NoteSavePillKind): boolean {
  return kind === 'pending' || kind === 'connecting';
}

function isWarnKind(kind: NoteSavePillKind): boolean {
  return kind === 'disconnected' || kind === 'offline';
}
